import { decimalCompare, decimalDivide, decimalMultiply, computedDecimalSchema, computedDecimalText, type DecimalValue } from "@shared/values/decimal";
import type { BadgeVariant } from "@/components/ui/badge";
import { INVENTORY_EFFECT_LABELS } from "@/lib/ui-labels";
import { getAllowedTransactionTypes } from "@shared/platform/access-control";
import {
  getTransactionCounterpartyKind,
  getTransactionLabel,
  TRANSACTION_TYPE_METADATA,
  TRANSACTION_TYPE_VALUES,
  type TransactionType,
} from "@shared/inventory/transaction-types";
import { formatMoney, formatRawDecimal } from "@shared/values/number-format";

export type TransactionPartyKind = "supplier" | "purchaser";

export const TRANSACTION_TYPE_DISPLAY = {
  PURCHASE: { variant: "default" },
  SALE: { variant: "secondary" },
  USAGE: { variant: "outline" },
  DISPOSAL: { variant: "destructive" },
  OPENING_BALANCE: { variant: "default" },
  CORRECTION: { variant: "outline" },
  BATCH_SPLIT: { variant: "secondary" },
} as const satisfies Record<TransactionType, {
  variant: BadgeVariant;
}>;

export type TransactionTypeKey = keyof typeof TRANSACTION_TYPE_DISPLAY;

export function transactionTypeLabel(key: string): string {
  return getTransactionLabel(key);
}

export function transactionTypeVariant(key: string): BadgeVariant {
  return key in TRANSACTION_TYPE_DISPLAY ? TRANSACTION_TYPE_DISPLAY[key as TransactionTypeKey].variant : "default";
}

export const TRANSACTION_TYPE_OPTIONS = TRANSACTION_TYPE_VALUES.map((value) => ({
  value,
  label: TRANSACTION_TYPE_METADATA[value].label,
}));

export type TransactionTypeOption = (typeof TRANSACTION_TYPE_OPTIONS)[number];

export function getTransactionTypeOptionsForRole(
  role: string | null | undefined,
): TransactionTypeOption[] {
  const allowedTypes = new Set(getAllowedTransactionTypes(role));
  return TRANSACTION_TYPE_OPTIONS.filter(({ value }) => allowedTypes.has(value));
}

const SYSTEM_ONLY_TRANSACTION_TYPES = new Set(["OPENING_BALANCE", "BATCH_SPLIT"]);

export function getManualTransactionTypeOptionsForRole(
  role: string | null | undefined,
): TransactionTypeOption[] {
  return getTransactionTypeOptionsForRole(role).filter(({ value }) => !SYSTEM_ONLY_TRANSACTION_TYPES.has(value));
}

export function getTransactionPartyKind(transactionType: string): TransactionPartyKind | null {
  return getTransactionCounterpartyKind(transactionType);
}

export function inventoryEffectLabel(inventoryApplied: boolean): string {
  return inventoryApplied ? INVENTORY_EFFECT_LABELS.applied : INVENTORY_EFFECT_LABELS.recordOnly;
}

export function inventoryEffectVariant(inventoryApplied: boolean): BadgeVariant {
  return inventoryApplied ? "default" : "outline";
}

type QuantityFacts = { transactionType: string; quantity: DecimalValue; unit: string; inventoryApplied?: boolean };

const SIGN_BY_STOCK_EFFECT: Partial<Record<string, number>> = { decrease: -1, increase: 1 };

export function signedTransactionQuantity({ transactionType, quantity }: QuantityFacts): string {
  const effect = TRANSACTION_TYPE_METADATA[transactionType as TransactionType]?.stockEffect;
  return decimalMultiply(SIGN_BY_STOCK_EFFECT[effect ?? ""] ?? 1, quantity);
}

export function formatTransactionQuantity(transaction: QuantityFacts): string {
  const signed = signedTransactionQuantity(transaction);
  const sign = decimalCompare(signed, 0) < 0 || signed.startsWith("-") ? "-" : "+";
  return `${sign}${formatRawDecimal(computedDecimalText(transaction.quantity).replace(/^[+-]/, ""))} ${transaction.unit}`;
}

export function formatTransactionRate(transaction: {
  price: string | number | null | undefined;
  quantity: string | number | null | undefined;
  unit: string | null | undefined;
}): string {
  if (transaction.price == null || transaction.quantity == null || !transaction.unit) {
    return "-";
  }

  const price = computedDecimalSchema.safeParse(transaction.price);
  const quantity = computedDecimalSchema.safeParse(transaction.quantity);

  if (!price.success || !quantity.success || decimalCompare(quantity.data, 0) <= 0) {
    return "-";
  }

  return `${formatMoney(decimalDivide(price.data, quantity.data))} / ${transaction.unit}`;
}
